// "Danışan bilgisi" — the subset form (day-view.md §7): contacts and
// the WhatsApp contact, phones, address, status, the free-text notes
// (read-modify-write against what the form loaded), the pinned desk
// note. Only what changed is sent; the use-case refuses a stale notes
// edit with a conflict, which the form says in one line.

import {useEffect, useState} from "react";
import {ApiRequestError, act, getClient} from "../api/client";
import type {ClientBasics, ClientStatus, UpdateClientBasicsInput} from "../api/contract";
import {useMoney} from "../ui/blind-context";
import {useToasts} from "../ui/toast-context";
import {Sheet} from "./Sheets";
import {errorText} from "./useActions";

type Draft = {
  status: ClientStatus;
  address: string;
  notes: string;
  messagingContactId: string | null;
  phones: Record<string, string>;
  relations: Record<string, string>;
  clearDeskNote: boolean;
};

const draftOf = (c: ClientBasics): Draft => ({
  status: c.status === "inactive" ? "inactive" : "active",
  address: c.address ?? "",
  notes: c.notes ?? "",
  messagingContactId: c.messagingContactId,
  phones: Object.fromEntries((c.contacts ?? []).map((k) => [k.contactId, k.phone])),
  relations: Object.fromEntries((c.contacts ?? []).map((k) => [k.contactId, k.relation])),
  clearDeskNote: false,
});

export function ClientSheet({clientId, demo, onClose, onSaved}: {
  clientId: string; demo?: ClientBasics; onClose: () => void; onSaved: () => void;
}) {
  const toast = useToasts();
  const {tl} = useMoney();
  const [loaded, setLoaded] = useState<ClientBasics | null>(demo ?? null);
  const [draft, setDraft] = useState<Draft | null>(demo ? draftOf(demo) : null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (demo) return;
    const ctl = new AbortController();
    getClient(clientId, ctl.signal).then(({data}) => {
      setLoaded(data);
      setDraft(draftOf(data));
    }).catch((e: unknown) => {
      if (!ctl.signal.aborted) setError(errorText(e));
    });
    return () => ctl.abort();
  }, [clientId, demo]);

  const save = async () => {
    if (!loaded || !draft) return;
    const body: UpdateClientBasicsInput = {clientId};
    if (draft.status !== (loaded.status === "inactive" ? "inactive" : "active")) {
      body.status = draft.status;
    }
    if (loaded.canEdit && draft.address !== (loaded.address ?? "")) body.address = draft.address;
    if (loaded.canEdit && draft.notes !== (loaded.notes ?? "")) {
      body.notes = {text: draft.notes, base: loaded.notes ?? ""};
    }
    if (draft.messagingContactId !== loaded.messagingContactId) {
      body.messagingContactId = draft.messagingContactId;
    }
    const contacts = loaded.contacts ?? [];
    const relationsChanged = contacts.some((k) => draft.relations[k.contactId] !== k.relation);
    if (relationsChanged) {
      body.contacts = contacts.map((k) => ({
        contactId: k.contactId, relation: draft.relations[k.contactId] || k.relation,
      }));
    }
    const phones = contacts.filter((k) => draft.phones[k.contactId] !== k.phone)
      .map((k) => ({contactId: k.contactId, phone: draft.phones[k.contactId]}));
    if (phones.length) body.contactPhones = phones;
    if (draft.clearDeskNote && loaded.deskNote) body.deskNote = null;

    if (Object.keys(body).length === 1) {
      toast.warn("Değişiklik yok.");
      return;
    }
    if (demo) {
      toast.ok(`DEMO — kaydedilmedi: ${Object.keys(body).filter((k) => k !== "clientId").join(", ")}`);
      onClose();
      return;
    }
    setSaving(true);
    try {
      const {data, warnings} = await act.updateClientBasics(body);
      toast.ok(data.updated ? `${data.clientName}: ${data.changed.join(", ")} güncellendi.` :
        `${data.clientName}: zaten böyleydi.`);
      for (const w of warnings) toast.warn(w);
      onSaved();
      onClose();
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === "conflict") {
        setError("Notlar bu arada başkası tarafından değişti — formu kapatıp yeniden açın.");
      } else {
        setError(errorText(e));
      }
    } finally {
      setSaving(false);
    }
  };

  const title = loaded ? <>Danışan bilgisi · {loaded.name}</> : "Danışan bilgisi";
  return (
    <Sheet title={title} onClose={onClose}>
      {error && <p className="banner error">{error}</p>}
      {!loaded || !draft ? (!error && <p className="muted">Yükleniyor…</p>) : (
        <form className="form" onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}>
          <div className="form-row">
            <span className="muted">Danışman</span>
            <span>{loaded.providerName}{loaded.sharedProviders.length > 0 &&
              <span className="muted"> · paylaşımlı</span>}</span>
          </div>
          {loaded.sessionFee !== null && (
            <div className="form-row">
              <span className="muted">Seans ücreti</span>
              <span>{tl(loaded.sessionFee)}</span>
            </div>
          )}
          <div className="form-row">
            <span className="muted">Sözleşme</span>
            <span>{loaded.sozlesme.text ?? "tamam"}
              {loaded.sozlesmeLink && <> · <a href={loaded.sozlesmeLink} target="_blank"
                rel="noreferrer">Aç ↗</a></>}</span>
          </div>
          <label className="form-row">
            <span className="muted">Durum</span>
            <select value={draft.status}
              onChange={(e) => setDraft({...draft, status: e.target.value as ClientStatus})}>
              <option value="active">aktif</option>
              <option value="inactive">pasif</option>
            </select>
          </label>
          {loaded.canEdit && (
            <label className="form-row">
              <span className="muted">Adres</span>
              <input className="text" value={draft.address}
                onChange={(e) => setDraft({...draft, address: e.target.value})} />
            </label>
          )}

          {loaded.contacts && (
            <fieldset className="contacts">
              <legend>Kişiler <span className="muted small">— WhatsApp onayı işaretli kişiye gider</span></legend>
              {loaded.contacts.length === 0 && <p className="muted">Bağlı kişi yok — sohbette ekleyin.</p>}
              {loaded.contacts.map((k) => (
                <div key={k.contactId} className="contact-row">
                  <label className="radio" title="WhatsApp onayı bu kişiye">
                    <input type="radio" name="messaging"
                      checked={draft.messagingContactId === k.contactId}
                      onChange={() => setDraft({...draft, messagingContactId: k.contactId})} />
                    <b>{k.name || k.contactId}</b>
                  </label>
                  <input className="text relation" value={draft.relations[k.contactId] ?? ""}
                    aria-label="Yakınlık" placeholder="anne / baba / kendisi"
                    onChange={(e) => setDraft({...draft,
                      relations: {...draft.relations, [k.contactId]: e.target.value}})} />
                  <input className="text phone" value={draft.phones[k.contactId] ?? ""}
                    aria-label="Telefon" placeholder="05xx xxx xx xx" inputMode="tel"
                    onChange={(e) => setDraft({...draft,
                      phones: {...draft.phones, [k.contactId]: e.target.value}})} />
                  <span className="muted small">
                    {k.whatsappConsent === "granted" ? "WA izni var" :
                      k.whatsappConsent ? `WA izni: ${k.whatsappConsent}` : "WA izni yok"}
                  </span>
                </div>
              ))}
              {draft.messagingContactId && (
                <button type="button" className="small-link"
                  onClick={() => setDraft({...draft, messagingContactId: null})}>
                  WhatsApp kişisini kaldır
                </button>
              )}
            </fieldset>
          )}

          {loaded.deskNote && (
            <div className="form-row">
              <span className="muted">Masa notu</span>
              <span>
                📝 {loaded.deskNote.text}
                <span className="muted small"> · {loaded.deskNote.by}</span>{" "}
                <label className="check inline">
                  <input type="checkbox" checked={draft.clearDeskNote}
                    onChange={(e) => setDraft({...draft, clearDeskNote: e.target.checked})} />
                  kaldır
                </label>
              </span>
            </div>
          )}

          {loaded.canEdit && (
            <label className="form-row col">
              <span className="muted">Notlar <span className="small">(serbest metin — tarihli not için "Not düş")</span></span>
              <textarea className="text" rows={3} value={draft.notes}
                onChange={(e) => setDraft({...draft, notes: e.target.value})} />
            </label>
          )}

          <div className="sheet-actions">
            <button type="button" className="big" onClick={onClose}>Vazgeç</button>
            <button type="submit" className="primary big" disabled={saving}>
              {saving ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        </form>
      )}
    </Sheet>
  );
}
